import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import type { Idea } from '@/lib/types';
import { GrowthIcon } from './GrowthIcon';
import { ProgressDots } from './ProgressDots';
import { getProgressDots, calculateDaysSinceWatered } from '@/lib/time-utils';
import { useIdeaUpdates } from '@/hooks/useIdeaUpdates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Droplets, Archive, Trash2, MessageSquare, Target, ListTodo, ExternalLink, Sparkles, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import {
  TEMPLATES,
  getTemplate,
  getSchemaForTemplate,
  createEmptyStructure,
  structureMatchesSchema,
  normalizeTemplateType,
  type TemplateType,
} from '@/lib/templates';

interface IdeaDetailSheetProps {
  idea: Idea | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: (id: string, updates: Partial<Idea>) => Promise<boolean>;
  onWater: () => void;
  onArchive: (id: string) => Promise<boolean>;
  onDelete: (id: string) => Promise<boolean>;
  onSetFocus: (id: string) => Promise<boolean>;
  onClearFocus: (id: string) => Promise<boolean>;
}

export function IdeaDetailSheet({
  idea,
  open,
  onOpenChange,
  onUpdate,
  onWater,
  onArchive,
  onDelete,
  onSetFocus,
  onClearFocus,
}: IdeaDetailSheetProps) {
  const navigate = useNavigate();
  const [editedFields, setEditedFields] = useState<Partial<Idea>>({});
  const [editedStructure, setEditedStructure] = useState<Record<string, string>>({});
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [localStructure, setLocalStructure] = useState<Record<string, string> | null>(null);
  const [localTemplateType, setLocalTemplateType] = useState<TemplateType | null>(null);
  const { updates, loading: updatesLoading } = useIdeaUpdates(idea?.id || null, 3);

  // Reset state when idea changes
  useEffect(() => {
    setEditedStructure({});
    setEditedFields({});
    setLocalStructure(null);
    setLocalTemplateType(null);
    setIsGenerating(false);
  }, [idea?.id]);

  // Sync local template type when prop updates
  useEffect(() => {
    if (idea?.template_type) {
      setLocalTemplateType(null); // clear override once prop catches up
    }
  }, [idea?.template_type]);

  if (!idea) return null;

  const isTree = idea.growth_stage === 'tree';
  const days = calculateDaysSinceWatered(idea);
  const dots = getProgressDots(idea);
  const templateType = localTemplateType ?? normalizeTemplateType(idea.template_type);
  const schema = getSchemaForTemplate(templateType);

  // Use localStructure (optimistic) if set, otherwise idea.structure
  const structure: Record<string, string> = localStructure
    ?? (idea.structure as Record<string, string>)
    ?? {};
  const isEmpty = !structure || Object.keys(structure).length === 0;
  const hasMismatch = !isEmpty && !structureMatchesSchema(structure, templateType);

  const handleFieldBlur = async (field: keyof Idea) => {
    if (editedFields[field] !== undefined && editedFields[field] !== idea[field]) {
      await onUpdate(idea.id, { [field]: editedFields[field] });
    }
    setEditedFields(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const getValue = (field: keyof Idea): string => {
    if (editedFields[field] !== undefined) return editedFields[field] as string;
    return (idea[field] as string) || '';
  };

  const handleChange = (field: keyof Idea, value: string) => {
    setEditedFields(prev => ({ ...prev, [field]: value }));
  };

  // Structure field handlers
  const getStructureValue = (key: string): string => {
    if (editedStructure[key] !== undefined) return editedStructure[key];
    return structure[key] || '';
  };

  const handleStructureChange = (key: string, value: string) => {
    setEditedStructure(prev => ({ ...prev, [key]: value }));
  };

  const handleStructureBlur = async (key: string) => {
    const currentVal = structure[key] || '';
    const editedVal = editedStructure[key];
    if (editedVal !== undefined && editedVal !== currentVal) {
      const newStructure = { ...structure, [key]: editedVal };
      await onUpdate(idea.id, { structure: newStructure });
    }
    setEditedStructure(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Format pill change
  const handleTemplateChange = async (newType: TemplateType) => {
    if (newType === templateType) return;
    setLocalTemplateType(newType); // instant UI update
    const success = await onUpdate(idea.id, { template_type: newType } as any);
    if (success) {
      toast.success(`Format changed to ${getTemplate(newType).label}`);
    } else {
      setLocalTemplateType(null); // rollback on failure
    }
  };

  // Generate / Regenerate
  const handleGenerateClick = () => {
    if (!isEmpty && !hasMismatch) {
      setShowRegenerateDialog(true);
    } else {
      doGenerate();
    }
  };

  const doGenerate = async () => {
    setIsGenerating(true);
    const keys = schema.map(f => f.key);
    // Show empty fields immediately as loading placeholders
    const placeholder = createEmptyStructure(templateType);
    setLocalStructure(placeholder);
    setEditedStructure({});

    try {
      const { data, error } = await supabase.functions.invoke('generate-structure', {
        body: {
          template_type: templateType,
          title: idea.title || '',
          brain_dump: idea.raw_input || '',
          keys,
        },
      });

      if (error) throw new Error(error.message || 'AI generation failed');
      if (data?.error) throw new Error(data.error);

      const filled: Record<string, string> = data?.structure ?? placeholder;
      // Ensure all keys present
      for (const k of keys) {
        if (typeof filled[k] !== 'string') filled[k] = '';
      }

      setLocalStructure(filled);
      const success = await onUpdate(idea.id, { structure: filled });
      if (success) {
        toast.success('Structure generated with AI');
      } else {
        toast.error('Failed to save structure');
      }
    } catch (err: any) {
      console.error('generate-structure error:', err);
      // Rollback
      setLocalStructure(null);
      toast.error(err?.message || 'Failed to generate structure');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerateConfirm = () => {
    setShowRegenerateDialog(false);
    doGenerate();
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-lg flex flex-col">
          <SheetHeader className="pb-2">
            <div className="flex items-center gap-3">
              <GrowthIcon stage={idea.growth_stage} size={28} />
              <div className="flex-1">
                <SheetTitle className="text-left">
                  {idea.title || 'Untitled Idea'}
                </SheetTitle>
                {!isTree && (
                  <div className="flex items-center gap-3 mt-1">
                    <ProgressDots count={dots} />
                    <span className="text-xs text-muted-foreground">
                      {days} day{days !== 1 ? 's' : ''} since last watered
                    </span>
                  </div>
                )}
              </div>
            </div>
          </SheetHeader>

          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-6">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={getValue('title')}
                  onChange={(e) => handleChange('title', e.target.value)}
                  onBlur={() => handleFieldBlur('title')}
                  placeholder="Give your idea a name..."
                />
              </div>

              {/* Format selector */}
              <div className="space-y-2">
                <Label>Format</Label>
                <div className="flex flex-wrap gap-1.5">
                  {TEMPLATES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => handleTemplateChange(t.key)}
                      className={cn(
                        'px-2.5 py-1 rounded-full text-xs font-medium transition-colors border',
                        templateType === t.key
                          ? 'bg-primary/10 border-primary/30 text-primary'
                          : 'bg-muted/30 border-border/40 text-muted-foreground hover:bg-muted/50'
                      )}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* Schema mismatch warning */}
                {hasMismatch && (
                  <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/10 rounded-md px-3 py-2 border border-destructive/20">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    Format changed. Generate structure to apply this format.
                  </div>
                )}

                {/* Generate / Regenerate CTA */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full gap-2 mt-2 text-xs"
                  onClick={handleGenerateClick}
                  disabled={isGenerating}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {isGenerating
                    ? 'Generating…'
                    : (isEmpty || hasMismatch)
                      ? 'Generate structure'
                      : 'Regenerate structure'}
                </Button>
              </div>

              {/* Schema-driven structure fields — always rendered from schema */}
              {!isEmpty && !hasMismatch && schema.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                      {getTemplate(templateType).label} Structure
                    </Label>
                    {schema.map((field) => (
                      <div key={field.key} className="space-y-1.5">
                        <Label htmlFor={`struct-${field.key}`} className="text-sm">
                          {field.label}
                        </Label>
                        <Textarea
                          id={`struct-${field.key}`}
                          value={getStructureValue(field.key)}
                          onChange={(e) => handleStructureChange(field.key, e.target.value)}
                          onBlur={() => handleStructureBlur(field.key)}
                          placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
                          className="resize-none"
                          rows={2}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}

              <Separator />

              {/* Recent Reflections */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <MessageSquare className="h-3.5 w-3.5" />
                  Recent Reflections
                </Label>
                {updatesLoading ? (
                  <p className="text-sm text-muted-foreground">Loading...</p>
                ) : updates.length === 0 ? (
                  <p className="text-sm text-muted-foreground/60 italic">
                    No reflections yet. Water this idea to add your first.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {updates.map((update) => (
                      <div
                        key={update.id}
                        className="bg-muted/30 rounded-lg p-3 space-y-1.5"
                      >
                        <p className="text-sm text-foreground/90 leading-relaxed">
                          {update.reflection}
                        </p>
                        {update.ai_summary && (
                          <p className="text-xs text-muted-foreground/70 italic border-l-2 border-primary/20 pl-2">
                            {update.ai_summary}
                          </p>
                        )}
                        <p className="text-[11px] text-muted-foreground/50">
                          {formatDistanceToNow(new Date(update.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Separator />

              {/* Open Plan link */}
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={() => {
                  onOpenChange(false);
                  navigate(`/plan/${idea.id}`);
                }}
              >
                <ListTodo className="h-4 w-4" />
                Open Plan
                <ExternalLink className="h-3 w-3 ml-auto" />
              </Button>

              <Separator />

              {/* Tags */}
              {idea.keywords_tags && idea.keywords_tags.length > 0 && (
                <div className="space-y-2">
                  <Label>Tags</Label>
                  <div className="flex flex-wrap gap-2">
                    {idea.keywords_tags.map((tag, i) => (
                      <Badge key={i} variant="secondary">{tag}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Raw Input */}
              <div className="space-y-2">
                <Label className="text-muted-foreground">Original Input</Label>
                <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                  {idea.raw_input}
                </p>
              </div>
            </div>
          </ScrollArea>

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t mt-4">
            <Button onClick={onWater} className="flex-1">
              <Droplets className="mr-2 h-4 w-4" />
              {isTree ? 'Deepen' : 'Water'}
            </Button>
            <Button
              variant={idea.is_focus ? 'default' : 'outline'}
              size="icon"
              onClick={() => (idea.is_focus ? onClearFocus(idea.id) : onSetFocus(idea.id))}
              title={idea.is_focus ? 'Remove focus' : 'Set as focus'}
            >
              <Target className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={() => onArchive(idea.id)}>
              <Archive className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                if (confirm('Delete this idea permanently?')) {
                  onDelete(idea.id);
                  onOpenChange(false);
                }
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Regenerate confirmation dialog */}
      <AlertDialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate in {getTemplate(templateType).label}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will replace your current structure. Your original input will be preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRegenerateConfirm}>Replace</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
